(() => {
  if (window.__gantriHeroScriptLoaded) return;
  window.__gantriHeroScriptLoaded = true;

  const SECTION_SELECTOR = '.gantri-hero[data-section-id]';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  const padNumber = (value) => String(value).padStart(2, '0');

  class GantriHero {
    constructor(element) {
      this.element = element;
      this.sectionId = element.dataset.sectionId;
      this.slides = Array.from(element.querySelectorAll('.gantri-hero__slide'));
      this.captionTarget = element.querySelector('[data-caption-target]');
      this.counterTarget = element.querySelector('[data-counter-target]');
      this.progressFill = element.querySelector('[data-progress-fill]');
      this.autoplayDuration = Number(element.dataset.autoplay) || 5000;
      this.autoplayEnabled = element.dataset.autoplayEnabled === 'true';
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.timer = null;
      this.isPausedByEditor = false;

      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);

      this.init();
    }

    get canAutoplay() {
      return this.autoplayEnabled && this.slides.length > 1 && !prefersReducedMotion.matches && !this.isPausedByEditor;
    }

    init() {
      if (!this.slides.length) return;

      this.goTo(this.currentIndex, false);

      if (this.canAutoplay) {
        this.start();
      } else {
        this.fillProgressComplete();
      }

      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (typeof prefersReducedMotion.addEventListener === 'function') {
        prefersReducedMotion.addEventListener('change', this.handleMotionChange);
      } else if (typeof prefersReducedMotion.addListener === 'function') {
        prefersReducedMotion.addListener(this.handleMotionChange);
      }
    }

    destroy() {
      this.stop();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (typeof prefersReducedMotion.removeEventListener === 'function') {
        prefersReducedMotion.removeEventListener('change', this.handleMotionChange);
      } else if (typeof prefersReducedMotion.removeListener === 'function') {
        prefersReducedMotion.removeListener(this.handleMotionChange);
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop(false);
        return;
      }

      if (this.canAutoplay) {
        this.start();
      }
    }

    handleMotionChange() {
      if (this.canAutoplay) {
        this.start();
      } else {
        this.stop(false);
        this.fillProgressComplete();
      }
    }

    goTo(index, restartCycle = true) {
      if (!this.slides.length) return;

      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      const activeSlide = this.slides[normalizedIndex];
      const nextCaption = activeSlide?.dataset.caption || '';

      if (this.captionTarget) {
        this.captionTarget.textContent = nextCaption;
      }

      if (this.counterTarget) {
        this.counterTarget.textContent = padNumber(normalizedIndex + 1);
      }

      if (restartCycle && this.canAutoplay) {
        this.start();
      }
    }

    start() {
      if (!this.canAutoplay) {
        this.fillProgressComplete();
        return;
      }

      this.stop(false);
      this.restartProgress();

      this.timer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1, false);
        this.start();
      }, this.autoplayDuration);
    }

    stop(resetProgress = true) {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }

      if (resetProgress && this.progressFill) {
        this.progressFill.style.transition = 'none';
        this.progressFill.style.transform = 'scaleX(0)';
      }
    }

    restartProgress() {
      if (!this.progressFill) return;

      this.progressFill.style.transition = 'none';
      this.progressFill.style.transform = 'scaleX(0)';
      this.progressFill.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        this.progressFill.style.transition = `transform ${this.autoplayDuration}ms linear`;
        this.progressFill.style.transform = 'scaleX(1)';
      });
    }

    fillProgressComplete() {
      if (!this.progressFill) return;

      this.progressFill.style.transition = 'none';
      this.progressFill.style.transform = 'scaleX(1)';
    }

    handleBlockSelect(blockId) {
      const blockIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (blockIndex === -1) return;

      this.isPausedByEditor = true;
      this.stop(false);
      this.goTo(blockIndex, false);
      this.fillProgressComplete();
    }

    handleSectionSelect() {
      this.isPausedByEditor = true;
      this.stop(false);
    }

    handleSectionDeselect() {
      this.isPausedByEditor = false;

      if (this.canAutoplay) {
        this.start();
      } else {
        this.fillProgressComplete();
      }
    }
  }

  function initGantriHeroes(scope = document) {
    const elements = scope.matches?.(SECTION_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SECTION_SELECTOR) || []);

    elements.forEach((element) => {
      if (instances.has(element)) return;
      instances.set(element, new GantriHero(element));
    });
  }

  function destroyGantriHeroes(scope = document) {
    const elements = scope.matches?.(SECTION_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SECTION_SELECTOR) || []);

    elements.forEach((element) => {
      const instance = instances.get(element);
      if (!instance) return;
      instance.destroy();
      instances.delete(element);
    });
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(() => initGantriHeroes(document));

  document.addEventListener('shopify:section:load', (event) => {
    initGantriHeroes(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyGantriHeroes(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const { sectionId, blockId } = event.detail || {};
    if (!sectionId || !blockId) return;

    instances.forEach((instance) => {
      if (instance.sectionId === sectionId) {
        instance.handleBlockSelect(blockId);
      }
    });
  });

  document.addEventListener('shopify:section:select', (event) => {
    const { sectionId } = event.detail || {};
    if (!sectionId) return;

    instances.forEach((instance) => {
      if (instance.sectionId === sectionId) {
        instance.handleSectionSelect();
      }
    });
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const { sectionId } = event.detail || {};
    if (!sectionId) return;

    instances.forEach((instance) => {
      if (instance.sectionId === sectionId) {
        instance.handleSectionDeselect();
      }
    });
  });
})();